import { supabase, Car, CarTask, LogEntry } from './supabase';
import { estimateDateAtMileage } from './mileageUtils';
import { format, addMonths } from 'date-fns';

/**
 * Syncs all car task reminders for a specific car based on latest mileage and history.
 */
export async function syncAllCarTaskReminders(carId: string) {
  const { data: car } = await supabase.from('cars').select('*').eq('id', carId).single();
  if (!car) return;

  const [
    { data: prefs },
    { data: tasks },
    { data: logEntries },
    { data: existingReminders }
  ] = await Promise.all([
    supabase.from('user_preferences').select('timezone').eq('user_id', car.user_id).maybeSingle(),
    supabase.from('car_tasks').select('*').eq('car_id', carId),
    supabase.from('log_entries').select('*').eq('car_id', carId).order('date', { ascending: false }),
    supabase.from('reminders').select('*').eq('car_id', carId).not('car_task_id', 'is', null)
  ]);

  const timezone = prefs?.timezone || 'UTC';
  if (!tasks || tasks.length === 0) return;

  const remindersMap = new Map();
  if (existingReminders) {
    existingReminders.forEach(r => {
      if (!remindersMap.has(r.car_task_id)) remindersMap.set(r.car_task_id, []);
      remindersMap.get(r.car_task_id).push(r);
    });
  }

  const promises: Promise<any>[] = [];
  
  for (const task of tasks) {
    const taskRems = remindersMap.get(task.id) || [];
    syncTaskRemindersLogic(car, task, logEntries || [], timezone, taskRems, promises);
  }

  if (promises.length > 0) {
    await Promise.all(promises);
  }
}

/**
 * Syncs reminders for a single car task (legacy wrapper for external callers)
 */
export async function syncTaskReminders(car: Car, task: CarTask, logEntries: LogEntry[]) {
  const { data: prefs } = await supabase.from('user_preferences').select('timezone').eq('user_id', car.user_id).single();
  const timezone = prefs?.timezone || 'UTC';
  
  const { data: existing } = await supabase.from('reminders').select('*').eq('car_task_id', task.id);
  const promises: Promise<any>[] = [];
  
  syncTaskRemindersLogic(car, task, logEntries, timezone, existing || [], promises);
  
  if (promises.length > 0) {
    await Promise.all(promises);
  }
}

function syncTaskRemindersLogic(
  car: Car, task: CarTask, logEntries: LogEntry[], timezone: string, 
  existingReminders: any[], promises: Promise<any>[]
) {
  const requiredReminders = new Map<string, any>();

  let startMileage = 0;
  let startDate: Date | null = null;

  if (task.history_confirmed) {
      startMileage = Number(task.last_performed_mileage) || 0;
      startDate = task.last_performed_date ? new Date(task.last_performed_date) : null;
  } else {
      startMileage = 0;
      if (car.year) startDate = new Date(car.year, 0, 1);
      else if (car.bought_at) startDate = new Date(car.bought_at);
  }

  if (task.reminder_mileage) {
    const isFirst = !task.last_performed_mileage && !task.last_performed_date && !task.history_confirmed;
    const interval = isFirst ? (Number(task.first_interval_distance) || Number(task.interval_distance) || 0) : (Number(task.interval_distance) || 0);
    
    if (interval > 0 || isFirst) {
        const nextMileage = startMileage + interval;
        const estimatedDate = estimateDateAtMileage(car, logEntries, nextMileage);
        
        if (estimatedDate) {
          const dateStr = format(estimatedDate, 'yyyy-MM-dd');
          requiredReminders.set(`[Mileage] ${task.name}`, {
             dateStr, desc: `Mileage target: ${nextMileage} ${car?.distance_unit || 'km'}`
          });
        }
    }
  }

  if (task.reminder_date && startDate) {
    const isFirst = !task.last_performed_date && !task.history_confirmed;
    const intervalMo = isFirst ? (Number(task.first_interval_months) || Number(task.interval_months) || 0) : (Number(task.interval_months) || 0);
    
    if (intervalMo > 0) {
        const nextDate = addMonths(startDate, intervalMo);
        const dateStr = format(nextDate, 'yyyy-MM-dd');
        requiredReminders.set(`[Date] ${task.name}`, {
           dateStr, desc: `Maintenance date: ${dateStr}`
        });
    }
  }

  const handledIds = new Set<string>();

  for (const [name, reqData] of requiredReminders.entries()) {
    const existing = existingReminders.find(r => r.name === name);
    const reminderData = {
      car_id: car.id,
      user_id: car.user_id,
      car_task_id: task.id,
      name: name,
      description: reqData.desc,
      remind_date: reqData.dateStr,
      remind_time: '12:00',
      is_active: true,
      is_seen: false
    };

    if (existing) {
      handledIds.add(existing.id);
      if (existing.remind_date !== reqData.dateStr || existing.description !== reqData.desc) {
        promises.push(supabase.from('reminders').update(reminderData).eq('id', existing.id));
      }
    } else {
      promises.push(supabase.from('reminders').insert(reminderData));
    }
  }

  const toDelete = existingReminders.filter(r => r.name && (r.name.startsWith('[Mileage]') || r.name.startsWith('[Date]')) && !handledIds.has(r.id));
  if (toDelete.length > 0) {
    const deleteIds = toDelete.map(r => r.id);
    promises.push(supabase.from('reminders').delete().in('id', deleteIds));
  }
}
